<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import { onBeforeRouteUpdate, useRouter, useRoute } from 'vue-router';
import { Header, CustomButton, ButtonType, NewEntryPopup, EntryList, Pipeline, CasesSearch, DataViewSwitcher } from '@/components';
import { CaseModel, EntryModel, FormModel, PumpPipelineFilter } from '@/models';
import { DATA_VIEW_FILTER, routeConstants, formattedDate, DATE_FORMAT_1, LocalStorageUtils } from '@/utils';
import SelectButton, { type SelectButtonChangeEvent } from 'primevue/selectbutton';
import Chip from 'primevue/chip';
import { useEntryListStore } from '@/stores/entryList';
import { useStudyStore } from '@/stores/study';
import { useFormStore } from '@/stores/forms';
import { useCaseStore } from '@/stores/case';
import { usePipelineStore } from '@/stores/pipeline';
import { requestForToken } from '@/services/firebase';

const entryStore = useEntryListStore();
const studyStore = useStudyStore();
const formStore = useFormStore();
const caseStore = useCaseStore();
const pipelineStore = usePipelineStore();
const caseSearchText = ref();
const headerInEditMode = ref(false);

const pumpFilterOptions = ref([PumpPipelineFilter.ACTIVE, PumpPipelineFilter.FINISHED, PumpPipelineFilter.ALL]);
const selectedPumpFilter = ref(pipelineStore.pumpFilter ?? PumpPipelineFilter.ACTIVE);
const router = useRouter();

onBeforeRouteUpdate(to => {
  caseStore.setSelectedCase(to.path, to.params.identifier as string);
});

watch(() => router.currentRoute.value, () => {
  setFilter();
});

const handleEnableNotifications = () => {
  requestForToken();
};

const setFilter = () => {
  const currentStudy = studyStore.studies.find(study => study.identifier === route.params.studyIdentifier);

  if (currentStudy?.flags?.preferences?.dataViewFilter) {
    if (selectedDataFilter.value !== currentStudy?.flags?.preferences?.dataViewFilter) {
      LocalStorageUtils.setDataViewFilter(currentStudy.identifier, currentStudy.flags.preferences.dataViewFilter);
      handleDataFilterSwitch(currentStudy.flags.preferences.dataViewFilter);
    }
  } else if (!currentStudy?.longitudinal) {
    if (selectedDataFilter.value !== DATA_VIEW_FILTER.ENTRIES) {
      handleDataFilterSwitch(DATA_VIEW_FILTER.ENTRIES);
    }
  } else if (route.fullPath.endsWith('cases')) {
    if (selectedDataFilter.value !== DATA_VIEW_FILTER.CASES) {
      handleDataFilterSwitch(DATA_VIEW_FILTER.CASES);
    }
  }
};

const dataSectionMounted = ref(false);
const route = useRoute();
onMounted(() => {
  setFilter();

  if (route.params.identifier && caseStore.caseList.length === 0 && !entryStore.selectedCase) {
    // this code block runs only when the page refreshed on this route, this will get required data for the entry list table
    (async () => {
      entryStore.entriesLoader.startLoading();
      await caseStore.getStudyCases(false, route.params.identifier as string, 'export');
      if (!entryStore.selectedCase) {
        getArchivedCases(false);
      }
      if (formStore.formList.length === 0) {
        await formStore.getStudyForms();
      }
      dataSectionMounted.value = true;
    })();
  } else if (route.params.identifier && !entryStore.selectedCase) {
    getArchivedCases(true);
  } else {
    dataSectionMounted.value = true;
  }

  
  setTimeout(() => {
    if (route.query.caseFilter) {
      casesSearch(route.query.caseFilter as string);
    }
  }, 100);
  
  handleEnableNotifications();
});

const getArchivedCases = (mountDataSection: boolean) => {
  // case could be archived, so looking for archived cases
  (async () => {
    await caseStore.getStudyCases(true, route.params.identifier as string);
    dataSectionMounted.value = mountDataSection;
  })();
};

const dataSectionMargin = computed(() => (entryStore.selectedCase ? '0px' : '20px'));

const onNewCase = async () => {
  resetSelectedResources();
  const caseData = await caseStore.createCase();
  router.push({
    name: routeConstants.CASE.name,
    params: {
      identifier: caseData.caseId
    }
  });
};

const selectedCases = ref<CaseModel[]>([]);
const selectedEntries = ref<EntryModel[]>([]);

const resetSelectedResources = () => {
  selectedCases.value = [];
  selectedEntries.value = [];
};

const handleLockUnlockResource = async (status: string) => {
  if (selectedCases.value.length > 0) {
    await caseStore.lockUnlockCases(selectedCases.value, status);
  } else if (selectedEntries.value.length > 0) {
    await entryStore.lockUnlockEntries(selectedEntries.value, status);
  }
  resetSelectedResources();
};

const handleArchiveResource = async () => {
  const errorObject: Error = {
    name: 'Locked',
    message: 'You have selected locked resources. Locked resources cannot be archived'
  };
  if (selectedCases.value.length > 0) {
    const lockedCases = selectedCases.value.find(caseItem => caseItem.lockedAt);
    if (lockedCases) {
      caseStore.caseActionLoader.setError(errorObject);
    } else {
      await caseStore.lockUnlockCases(selectedCases.value, 'archive');
    }
  } else {
    const lockedEntries = selectedEntries.value.find(entry => entry.lockedAt);
    if (lockedEntries) {
      caseStore.caseActionLoader.setError(errorObject);
    } else {
      await entryStore.lockUnlockEntries(selectedEntries.value, 'archive');
    }
  }
  resetSelectedResources();
}; 

const onBackClick = () => {
  resetSelectedResources();
  entryStore.resetData();
  router.back();
};
const navigateToArchive = () => {
  router.push({ name: routeConstants.ARCHIVE.name });
};

const selectedDataFilter = ref(
  route.fullPath.endsWith('pipeline') ?
    DATA_VIEW_FILTER.PIPELINE :
    route.fullPath.endsWith('cases') ?
      DATA_VIEW_FILTER.CASES :
      LocalStorageUtils.getCurrentDataViewFilter(route.params.studyIdentifier as string) || DATA_VIEW_FILTER.ENTRIES
);
const handleDataFilterSwitch = (filter: string) => {
  selectedDataFilter.value = filter;
  router.replace({
    name: selectedDataFilter.value === DATA_VIEW_FILTER.CASES ? routeConstants.CASES.name :
      selectedDataFilter.value === DATA_VIEW_FILTER.PIPELINE ? routeConstants.CASES_PIPELINE.name :
        routeConstants.CASE_ENTRIES.name,
    params: {
      studyIdentifier: route.params.studyIdentifier
    }
  });
  resetSelectedResources();
};

const handleCaseClick = () => {
  resetSelectedResources();
};

const onCaseUpdate = async (caseName: string) => {
  headerInEditMode.value = true;
  const updated = await caseStore.updateCase(entryStore.selectedCase!.identifier, caseName);
  if (updated) {
    entryStore.selectedCase!.label = caseName;
    headerInEditMode.value = false;
  }
};

const handleFormOpen = (form: FormModel) => {
  resetSelectedResources();
  if (entryStore.selectedCase) {
    router.push({
      name: routeConstants.CASE_NEW_ENTRY.name,
      params: {
        identifier: entryStore.selectedCase.identifier,
        formIdentifier: form.identifier
      }
    });
  } else {
    router.push({
      name: routeConstants.NEW_ENTRY.name,
      params: {
        formIdentifier: form.identifier
      }
    });
  }
};

const casesSearch = (searchQuery: string) => {
  caseSearchText.value = searchQuery;
  if (selectedDataFilter.value === DATA_VIEW_FILTER.CASES) {
    caseStore.handleCaseSearch(searchQuery);
  } else if (selectedDataFilter.value === DATA_VIEW_FILTER.PIPELINE) {
    pipelineStore.handleCaseSearch(searchQuery);
  }
  if (searchQuery) {
    router.replace({ query: { caseFilter: searchQuery } });
  } else {
    router.replace({ query: {} });
  }
};

const onRemoveChipEvent = () => {
  casesSearch('');
};

const handlePumpPerfusionFilterChange = (filter: SelectButtonChangeEvent) => {
  selectedPumpFilter.value = filter.value;
  pipelineStore.pumpFilter = filter.value;
  pipelineStore.currentPage = 1;
  // this will clear the existing case search filter (chip) while changing the perfusion filter
  casesSearch('');
};

const emptyContentMessage = computed(() => {
  if (pipelineStore.isPumpStudy) {
    if (selectedPumpFilter.value === PumpPipelineFilter.ACTIVE) {
      return 'There are no active perfusions at the moment.';
    } else if (selectedPumpFilter.value === PumpPipelineFilter.FINISHED) {
      return 'No perfusions have been completed yet.';
    } else {
      return 'No perfusions have been started yet.';
    }
  }
  return 'No Cases created so far';
});
</script>

<template>
  <div class="data-section">
    <div class="data-wrapper" v-if="!studyStore.studyLoader.isLoading">
      <div class="header">
        <Header :title="entryStore.selectedCase ? `${entryStore.selectedCase.label}` : 'Data'"
          :description="
            entryStore.selectedCase
              ? formattedDate(entryStore.selectedCase.createdAt, DATE_FORMAT_1)
              : undefined
          "
          :showBack="entryStore.selectedCase !== undefined"
          :showEdit="entryStore.selectedCase && !entryStore.selectedCase.lockedAt &&
            !entryStore.selectedCase.archivedAt && studyStore.selectedStudy?.flags?.customDataDictionary !== 'PUMP'"
          :locked="entryStore.selectedCase?.lockedAt != undefined"
          :archived="entryStore.selectedCase?.archivedAt != undefined"
          :headerInEditMode="headerInEditMode"
          @onTitleSave="onCaseUpdate"
          @onBackClick="onBackClick">
          <template #headerButtons>
            <div class="header-buttons">
              <div class="select-buttons"
                v-if="
                  selectedCases.length > 0 &&
                  !caseStore.caseActionLoader.isLoading
                ">
                <CustomButton text="Archive"
                  :type="ButtonType.OUTLINED"
                  :margin-left="16"
                  v-if="studyStore.studyPermissions?.editCases"
                  :icon="{ icon: 'box-archive', style: 'far' }"
                  @onClick="handleArchiveResource"/>

                <CustomButton text="Lock"
                  :type="ButtonType.OUTLINED"
                  :margin-left="16"
                  v-if="studyStore.studyPermissions?.manageInvest || studyStore.studyPermissions?.editCases"
                  :icon="{ icon: 'lock', style: 'far' }"
                  @onClick="handleLockUnlockResource('locked')"/>

                <CustomButton text="Unlock"
                  :type="ButtonType.OUTLINED"
                  :margin-left="16"
                  v-if="studyStore.studyPermissions?.manageInvest || studyStore.studyPermissions?.editCases"
                  :icon="{ icon: 'unlock', style: 'far' }"
                  @onClick="handleLockUnlockResource('unlocked')"/>
              </div>
              <CustomButton text="Restore"
                  :type="ButtonType.OUTLINED"
                  :margin-left="16"
                  v-if="entryStore.selectedCase?.archivedAt && !caseStore.caseActionLoader.isLoading"
                  @onClick="caseStore.handleCaseRestore"
                  :icon="{ icon: 'arrow-rotate-left', style: 'far' }"/>
              <div v-if="
                  caseStore.caseActionLoader.isLoading ||
                  entryStore.entryActionLoader.isLoading
                "
                class="progress-container">
                <ProgressSpinner strokeWidth="4" />
              </div>
              <CustomButton text="View Archives"
                class="archive-button"
                :type="ButtonType.OUTLINED"
                :icon="{ icon: 'archive', style: 'far' }"
                @onClick="navigateToArchive" />
              <CustomButton text="New case"
                :type="ButtonType.POSITIVE"
                v-if="
                  selectedCases.length == 0 &&
                  selectedEntries.length == 0 &&
                  !entryStore.selectedCase &&
                  studyStore.selectedStudy?.longitudinal &&
                  studyStore.studyPermissions?.editCases
                "
                :icon="{ icon: 'folder', style: 'far' }"
                @onClick="onNewCase"/>
              <div class="entry-buttons" v-if="entryStore.selectedCase || selectedEntries.length > 0">
                <div class="select-buttons"
                  v-if="
                    selectedEntries.length > 0 &&
                    !entryStore.entryActionLoader.isLoading
                  ">
                  <CustomButton text="Archive"
                    :type="ButtonType.OUTLINED"
                    :margin-left="16"
                    :icon="{ icon: 'box-archive', style: 'far' }"
                    @onClick="handleArchiveResource"/>

                  <CustomButton text="Lock"
                    :type="ButtonType.OUTLINED"
                    :margin-left="16"
                    v-if="studyStore.studyPermissions?.manageInvest || studyStore.studyPermissions?.editCases"
                    :icon="{ icon: 'lock', style: 'far' }"
                    @onClick="handleLockUnlockResource('locked')"/>

                  <CustomButton text="Unlock"
                    :type="ButtonType.OUTLINED"
                    :margin-left="16"
                    v-if="studyStore.studyPermissions?.manageInvest || studyStore.studyPermissions?.editCases"
                    :icon="{ icon: 'unlock', style: 'far' }"
                    @onClick="handleLockUnlockResource('unlocked')"/>
                </div>
              </div>
              <NewEntryPopup :forms="formStore.formList"
                :entries="entryStore.entryList"
                :margin-left="16"
                @onFormClick="handleFormOpen"
                v-if="
                  studyStore.selectedStudy?.longitudinal === true && !entryStore.selectedCase?.archivedAt
                    ? selectedEntries.length === 0 && entryStore.selectedCase && entryStore.selectedCase?.lockedAt == undefined
                    : selectedEntries.length === 0 && !studyStore.selectedStudy?.longitudinal
                "/>
            </div>
          </template>
        </Header>

        <div class="header-first" v-if="!entryStore.selectedCase">
          <div class="header-extras">
            <DataViewSwitcher @onDataSwitch="handleDataFilterSwitch"
            v-if="studyStore.selectedStudy?.longitudinal === true"/>
            <CasesSearch @onSearch="casesSearch"
              v-if="selectedDataFilter === DATA_VIEW_FILTER.CASES || selectedDataFilter === DATA_VIEW_FILTER.PIPELINE"/>
          </div>
          
          <div class="header-extra-filter"  v-if="selectedDataFilter === DATA_VIEW_FILTER.PIPELINE">
            <div class="empty" v-if="!caseSearchText"/>
            <div class="case-filter" v-if="caseSearchText">
              <Chip :label="caseSearchText" removable @remove="onRemoveChipEvent"/>
              <div class="selection-text"> {{ pipelineStore.caseList.length }} out of {{ pipelineStore.totalCasesCount }} selected</div>
            </div>
            <div class="perfusion-filter" v-if="caseStore.isPumpStudy">
              <div class="perfusion-filter-header">Perfusion filter:</div>
              <SelectButton v-model="selectedPumpFilter"
              :options="pumpFilterOptions"
              @change="handlePumpPerfusionFilterChange"/>
            </div>
          </div>
        </div>
      </div>

      <div class="data-section-container" v-if="dataSectionMounted">
        <RouterView v-model:selectedCases="selectedCases"
          v-model="selectedEntries"
          @onNewCase="onNewCase"
          @onCaseClick="handleCaseClick"
          v-if="selectedDataFilter === DATA_VIEW_FILTER.CASES || entryStore.selectedCase"/>

        <EntryList v-model="selectedEntries"
          v-if="selectedDataFilter === DATA_VIEW_FILTER.ENTRIES && !entryStore.selectedCase"/>

        <Pipeline v-model:selectedCases="selectedCases"
          v-if="selectedDataFilter === DATA_VIEW_FILTER.PIPELINE && !entryStore.selectedCase"
          :emptyContentMessage="emptyContentMessage"/>
      </div>

      <div class="data-section-progress" v-if="!dataSectionMounted">
        <ProgressSpinner strokeWidth="4" />
      </div>
    </div>

    <div class="data-section-progress" v-if="studyStore.studyLoader.isLoading">
      <ProgressSpinner strokeWidth="4" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '../../assets/variables.module';
.data-section {
  padding: 48px 100px 40px 80px;
  height: 100%;
  min-width: 1200px;
}
.data-section-progress {
  display: flex;
  align-items: center;
  height: 100vh;
  width: 100%;
  justify-content: center;
}
.data-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.data-section-container {
  flex: 1;
  overflow: hidden;
  margin-top: v-bind(dataSectionMargin);
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
.data-section-progress {
  flex: 1;
  display: flex;
  align-items: center;
}
.header-buttons,
.entry-buttons {
  display: flex;
}
.select-buttons {
  display: flex;
}
.progress-container {
  display: flex;
  justify-content: center;

  :deep(.p-progressspinner) {
    height: 32px;
    width: 32px;
  }
}
.header-extras {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
:deep(.p-row-even) {
  cursor: pointer;
}
:deep(.p-row-odd) {
  cursor: pointer;
}
.header-extra-filter {
  width: 100%;
  display: flex;
  margin-top: 8px;
  justify-content: space-between;
  align-items: center;
}
.perfusion-filter {
  display: flex;
  align-items: center;
}
.perfusion-filter-header {
  font-weight: 600;
  font-size: 14px;
}
:deep(.p-selectbutton) {
  margin-left: 8px;
}
:deep(.p-togglebutton.p-togglebutton-checked::before) {
  background: $primary-color;
}
:deep(.p-togglebutton.p-togglebutton-checked) {
  color: white;
}
:deep(.p-chip) {
  background: $primary-color;
  color: white;
}
.case-filter {
  display: flex;
  align-items: center;
}
.selection-text {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
}
.archive-button {
  margin-right: 16px;
}
</style>
